<template>
  <div class="ehs-data-table relative">
    <EHSTableToolbar :selectable="selectable" :total-selected="totalSelected">
      <template #selection-actions>
        <slot name="selection-actions" v-bind="{ selectedItems }" />
      </template>
      <template #actions>
        <slot name="actions" />
      </template>
    </EHSTableToolbar>
    <EHSTableHeader :columns="columns" :selectable="selectable"></EHSTableHeader>
    <div
      v-if="tableRowData.length < 1"
      data-w-element="content"
      class="pt-16 px-4 pb-4 text-center"
    >
      {{ $t('wizer.components.data-table.no-data-to-display') }}
    </div>
    <div class="min-w-full">
      <EHSEditableRow
        :columns="columns"
        v-for="(row, i) in tableRowData"
        :key="i + uniqueId"
        :rowNumber="i + 1"
        :rowData="row"
        :allRows="tableRowData"
        :editMode="true"
        :isLastRow="i + 1 === tableRowData.length"
        :tabThroughAction="tabThroughAction"
        :selectable="selectable"
        :validateCell="validateCell"
        :isCellInvalid="isCellInvalid"
        @toggle-select="toggleSelect"
        @rowUpdated="onRowUpdated"
        ref="EHSTableRow"
      ></EHSEditableRow>
    </div>
  </div>
</template>
<script>
import { contains } from '@wizer/core/utils'
import _ from 'lodash'
import EHSEditableRow from '@/components/table/EHSEditableRow'
import EHSTableHeader from '@/components/table/EHSTableHeader'
import EHSTableToolbar from '@/components/table/EHSTableToolbar'

export default {
  name: 'EHSTable',
  components: {
    EHSTableHeader,
    EHSEditableRow,
    EHSTableToolbar,
  },
  props: {
    rowKeyAttrib: String,
    columns: Array,
    tableRowData: Array,
    tabThroughAction: Function,
    updateAction: Function,
    isWholeRowNotEmpty: Function,
    /**
     * Make rows selectable
     */
    selectable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedItems: [],
      // this number is used as part of the row key, which will cause the rows to be re-rendered on
      // row deselection/deletion
      uniqueId: _.uniqueId(),
      validationMessage: false,
      rowValidationStatus: {},
    }
  },
  computed: {
    totalSelected() {
      return this.selectedItems.length
    },
  },
  methods: {
    isRequiredCellEmpty(column, row) {
      if (!column.required) {
        return false
      }
      return (
        !row[column.property] &&
        `Failed to Add the Task Because Some Required Cells in '${column.label}' Column Are Empty`
      )
    },
    isCellLengthInvalid(column, row) {
      if (!column.maxLength) {
        return false
      }
      return (
        row[column.property].length > column.maxLength &&
        `Failed to Add the Task Because Some Cells in '${column.label}' Column Exceed the Maximum Length`
      )
    },
    columnValueIsNotUnique(column, row) {
      if (!column.unique) {
        return false
      }
      const seen = new Set()
      return (
        this.tableRowData.some(
          (currentObject) =>
            currentObject[column.property] === row[column.property] &&
            seen.size === seen.add(currentObject[column.property]).size,
        ) &&
        `Failed to Add the Task Because Some Cell Values in '${column.label}' Column are not Unique`
      )
    },
    getCellId(column, row) {
      return `${column.property}-${row.dataTableId}`
    },
    isCellInvalid(column, row) {
      return (
        this.isWholeRowNotEmpty(row) &&
        (this.isRequiredCellEmpty(column, row) ||
          this.isCellLengthInvalid(column, row) ||
          this.columnValueIsNotUnique(column, row))
      )
    },
    validateCell(column, row) {
      const cellId = this.getCellId(column, row)
      const validationMessage = this.isCellInvalid(column, row)
      if (validationMessage) {
        this.rowValidationStatus[cellId] = validationMessage
      } else {
        this.rowValidationStatus[cellId] = false
      }
    },
    revalidate() {
      this.rowValidationStatus = {}
      this.tableRowData.forEach((row) => {
        this.columns.forEach((column) => {
          this.validateCell(column, row)
        })
      })
    },
    onRowUpdated(isInitialUpdate) {
      this.revalidate()
      const messages = Object.keys(this.rowValidationStatus)
        .map((key) => this.rowValidationStatus[key])
        .filter((message) => !!message)
      // Finds the first validation message to show in an error notification
      this.validationMessage = messages[0]
      if (!isInitialUpdate) {
        this.updateAction()
      }
    },
    toggleSelect(item) {
      const match = contains(this.selectedItems, item)
      if (match) {
        this.selectedItems = _.without(this.selectedItems, match)
      } else {
        this.selectedItems.push(item)
      }
      this.emitSelected()
    },
    /**
     * Removes all items from active list
     */
    deselectAll() {
      this.selectedItems = []
      this.uniqueId = _.uniqueId()
      this.emitSelected()
    },
    /**
     * Emits update:selected event
     */
    emitSelected() {
      /**
       * Triggered when selected items changes
       *
       * @event update:selected
       * @type {Array}
       */
      this.$emit('update:selected', this.selectedItems)
    },
  },
}
</script>

<style scoped>
.ehs-data-table {
  @apply flex flex-col flex-grow w-full rounded;
  @apply text-sm text-gray-700 tracking-wide;
  @apply bg-white overflow-auto;
  @apply relative;
}
</style>
